import React, { useContext, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '../ThemeContext';
import { Clock, Laptop, Target, BookOpen, HelpCircle, TrendingUp } from 'lucide-react';

// Module-level animation state tracking
const cardAnimationState = new Map();

// Card component moved outside to prevent re-creation
const Card = ({ children, className = '', delay = 0, cardId }) => {
  const hasAnimated = cardAnimationState.has(cardId) ? cardAnimationState.get(cardId) : false;

  React.useEffect(() => {
    if (!hasAnimated) {
      cardAnimationState.set(cardId, true);
    }
  }, [cardId, hasAnimated]);

  return (
    <motion.div
      initial={hasAnimated ? false : { opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: hasAnimated ? 0 : delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 md:p-8 border border-white/20 dark:border-gray-700/50 ${className}`}
      style={{
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      }}
    >
      {children}
    </motion.div>
  );
};

const SATPage = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const navigate = useNavigate();

  // Memoize title gradient to update when dark mode changes
  const titleGradient = useMemo(() => {
    const color = isDarkMode ? '#ff7a00' : '#212D82';
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);

  const sectionHeadingGradient = useMemo(() => {
    const color = isDarkMode ? '#ff7a00' : '#212D82';
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);

  const digitalSATFeatures = [
    {
      feature: 'Test Length',
      details: 'Approximately 2 hours and 14 minutes (Significantly shorter than the old paper test).',
      keywords: 'Digital SAT length, new SAT format',
      icon: <Clock size={24} />
    },
    {
      feature: 'Format',
      details: 'Adaptive. The difficulty of the second module in each section (Math and Reading/Writing) adjusts based on your performance in the first.',
      keywords: 'Adaptive SAT, SAT sections',
      icon: <Laptop size={24} />
    },
    {
      feature: 'Scoring',
      details: 'Scored on a scale of 400–1600.',
      keywords: 'SAT score range, highest SAT score',
      icon: <Target size={24} />
    },
    {
      feature: 'Sections',
      details: 'Two main sections: Reading and Math.',
      keywords: 'SAT Math prep, SAT Reading test',
      icon: <BookOpen size={24} />
    },
  ];

  const faqs = [
    {
      question: 'What skills does the SAT test for?',
      answer: 'The SAT primarily tests the skills you\'ve learned in high school, focusing on critical reading, writing proficiency, and mathematical reasoning. It measures your ability to analyze, synthesize, and solve problems under timed conditions.'
    },
    {
      question: 'How can Game Your SAT help me prepare?',
      answer: 'Game Your SAT offers a gamified, ranking study experience that focuses on targeted practice based on your opportunities. Our unique gaming design system keeps you engaged and on track to level up your SAT score! You also can take full-length, timed and scored practice tests. Our diagnostics analyze your game performance and practice tests and tell you where your strong and weak areas are.'
    },
    {
      question: 'When should I take the SAT?',
      answer: 'Most students take the exam in the spring of their junior year (11th grade) or the fall of their senior year (12th grade). Taking it early allows time for a retake if necessary to improve your score.'
    },
    {
      question: 'Is the SAT required for college admissions?',
      answer: 'While many schools have adopted "test-optional" policies, a strong SAT score can significantly boost your application and may be required for certain scholarships or specialized programs. Check the requirements of your target universities.'
    },
    {
      question: 'How is the Digital SAT different from the paper test?',
      answer: 'The key differences are:\n\n• Shorter duration.\n\n• Adaptive testing (difficulty changes mid-test).\n\n• All tests are taken on a computer or tablet.\n\n• Basic calculators are allowed for the entire Math section.'
    },
  ];

  return (
    <div className={`min-h-screen pt-14 pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900`}>
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6 md:py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="text-center mb-8 md:mb-12"
        >
          <h1
            key={`sat-title-${isDarkMode}`}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold font-zonaProUltra mb-4 relative inline-block"
            style={{
              background: titleGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            What is the SAT? Your Essential Guide
          </h1>
        </motion.div>

        {/* Introduction Section */}
        <Card delay={0.1} cardId="introduction" className="mb-8 md:mb-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <p className="text-base sm:text-lg md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed mb-4 text-left">
              The SAT  is a standardized exam widely used by colleges and universities in the United States and Canada during admissions. It assesses a student's readiness for college-level work and is one of the most critical factors in a successful college application.            </p>
          </motion.div>
        </Card>

        {/* Key Facts: The Digital SAT Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-8 md:mb-12"
        >
          <h2
            key={`digital-sat-title-${isDarkMode}`}
            className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra mb-6 text-center relative inline-block w-full"
            style={{
              background: sectionHeadingGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            Key Facts: The Digital SAT
          </h2>
          <p className="text-center text-base sm:text-lg md:text-xl text-gray-700 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
            Since 2024, the SAT has transitioned to a new Digital SAT format. <span className="font-bold font-zonaProUltra">Game Your SAT</span> is specifically designed to prepare students for this new, modern version of the test.
          </p>

          {/* Features Table */}
          <div className="overflow-x-auto">
            <Card delay={0.2} cardId="features-table" className="p-0">
              <div className="overflow-hidden rounded-3xl">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300 font-zonaProUltra">
                        Feature
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300 font-zonaProUltra">
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {digitalSATFeatures.map((item, index) => (
                      <motion.tr
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="text-[#06bae4]">
                              {item.icon}
                            </div>
                            <span className="text-sm sm:text-base font-bold font-zonaProUltra text-gray-900 dark:text-gray-100">
                              {item.feature}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-zonaProRegular">
                            {item.details}
                          </span>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </motion.div>

        {/* FAQs Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-8 md:mb-12"
        >
          <h2
            key={`faqs-title-${isDarkMode}`}
            className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra mb-8 text-center relative inline-block w-full"
            style={{
              background: sectionHeadingGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            SAT Frequently Asked Questions (FAQs)
          </h2>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <Card key={index} delay={0.3 + index * 0.1} cardId={`faq-${index}`}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-lg bg-[#06bae4]/10 dark:bg-[#06bae4]/20 flex-shrink-0">
                      <HelpCircle size={24} className="text-[#06bae4]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg sm:text-xl md:text-2xl font-bold font-zonaProUltra text-gray-800 dark:text-gray-200 mb-3">
                        {faq.question}
                      </h3>
                      <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-line text-left">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </Card>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <Card delay={0.8} cardId="cta-section" className="text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 rounded-full bg-[#06bae4]/10 dark:bg-[#06bae4]/20">
                <TrendingUp size={48} className="text-[#06bae4]" />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <motion.button
                onClick={() => navigate('/signup')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-gradient-to-r from-[#06bae4] to-[#06bae4] hover:from-[#05a5d1] hover:to-[#05a5d1] text-white font-bold font-zonaProUltra rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-lg md:text-xl"
              >
                Play Now
              </motion.button>
              <motion.button
                onClick={() => navigate('/subscription')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-gradient-to-r from-[#212D82] to-[#212D82] dark:from-[#ff7a00] dark:to-[#ff7a00] hover:from-[#1a2469] hover:to-[#1a2469] dark:hover:from-[#e66900] dark:hover:to-[#e66900] text-white font-bold font-zonaProUltra rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-lg md:text-xl"
              >
                Level-up Your Subscription
              </motion.button>
            </div>
          </motion.div>
        </Card>
      </div>
    </div>
  );
};

export default SATPage;

